import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, Check, Bell, Award } from 'lucide-react';

const NewsletterForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscribers, setSubscribers] = useState<string[]>([]);

  // Load existing subscribers from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('505_foods_subscribers');
      if (stored) {
        setSubscribers(JSON.parse(stored));
      } else {
        // Seed with a few realistic mock subscribers for visual richness
        const seeds = ['chef.gabriel@cuisine.co', 'nm_chile_fan@abqmail.com', 'santafe_foodie@vistas.net'];
        localStorage.setItem('505_foods_subscribers', JSON.stringify(seeds));
        setSubscribers(seeds);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !email.includes('@')) {
      setError('Please provide a valid culinary email address.');
      return;
    }

    if (subscribers.includes(email)) {
      setError('You are already on the 505 VIP taste list!');
      return;
    }

    const updatedSubscribers = [...subscribers, email];
    setSubscribers(updatedSubscribers);
    localStorage.setItem('505_foods_subscribers', JSON.stringify(updatedSubscribers));
    setSubscribed(true);
    setEmail('');
    setName('');
  };

  return (
    <div className="w-full max-w-lg mx-auto bg-white/80 border border-stone-200/80 backdrop-blur-md rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden" id="newsletter-form">
      {/* Visual Accent Top Line */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red-600 via-amber-500 to-green-700" />
      
      <AnimatePresence mode="wait">
        {!subscribed ? (
          <motion.div
            key="subscribe-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            <div className="text-center space-y-2 mb-4">
              <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-900 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase mb-1">
                Join the VIP Taste Circle
              </span>
              <h3 className="text-2xl font-bold tracking-tight text-stone-900 serif">
                Unlock Early Previews & Launch Invites
              </h3>
              <p className="text-sm text-stone-600 max-w-sm mx-auto">
                Be the first to know when our kitchen fires up, and get exclusive tickets to our preview tasting gala in ABQ.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 pt-2">
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-widest mb-1">
                  Your Full Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="E.g., Gabriel Romero"
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-red-600/30 focus:border-red-600 bg-stone-50/50 text-sm transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-widest mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
                    <Mail className="h-4 w-4 text-stone-400" />
                  </span>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@domain.com"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-red-600/30 focus:border-red-600 bg-stone-50/50 text-sm transition-all"
                  />
                </div>
              </div>

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-red-600 font-medium pl-1"
                >
                  {error}
                </motion.p>
              )}

              <button
                type="submit"
                className="w-full bg-red-700 hover:bg-red-800 text-white font-semibold text-sm py-3 px-6 rounded-xl transition-all shadow-md active:scale-[0.98] flex items-center justify-center gap-2 mt-4 cursor-pointer"
              >
                <Bell className="w-4 h-4" />
                Reserve Tasting Seat
              </button>
            </form>

            {/* Current waitlist counter */}
            <div className="pt-4 border-t border-stone-100 flex items-center justify-center gap-2 text-xs text-stone-500">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-600 animate-ping" />
              <span>Join <strong className="text-stone-800 font-semibold">{subscribers.length + 42} foodies</strong> already waiting for launch</span>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="subscribe-success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6 space-y-4"
          >
            <div className="mx-auto w-16 h-16 bg-red-50 text-red-700 flex items-center justify-center rounded-full shadow-inner">
              <Award className="w-8 h-8" />
            </div>
            
            <div className="space-y-1">
              <h3 className="text-2xl font-bold tracking-tight text-stone-900 serif">
                You're officially on the List!
              </h3>
              <p className="text-sm text-stone-600 max-w-sm mx-auto">
                Thank you, {name || 'Culinary Lover'}! Your early-access seat has been secured. We will notify you at your registered email when preview dining launches!
              </p>
            </div>

            <div className="bg-stone-50 border border-stone-150 rounded-2xl p-4 text-left max-w-sm mx-auto">
              <span className="text-[10px] font-bold text-premium uppercase tracking-widest text-red-700 block mb-1">
                Your Invitation Pass Details
              </span>
              <div className="text-xs text-stone-500 space-y-1">
                <p>📍 Location: Downtown Albuquerque, NM</p>
                <p>✨ Member ID: #505-Taste-{subscribers.length + 42}</p>
                <p>🌶️ Level Achieved: Early Tasting Circle Member</p>
              </div>
            </div>

            <button
              onClick={() => setSubscribed(false)}
              className="text-xs text-stone-500 hover:text-red-700 underline underline-offset-4 font-medium transition-colors cursor-pointer"
            >
              Sign up another email
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsletterForm;
